import type {
  ProductKnowledgeDetailByType,
  ProductKnowledgeDetailPage,
  ProductKnowledgeProductPage,
  ProductKnowledgeProductSearchInput,
  ProductKnowledgeScopeMutationInput,
  ProductKnowledgeScopeSearchInput,
  ProductKnowledgeScopeSearchResult,
  ProductKnowledgeScopeSnapshot,
  ProductKnowledgeSearchInput,
  ProductKnowledgeType,
  ProductKnowledgeUpdateInputByType,
  ProductKnowledgePickerInput,
} from '../model/types';
import { knowledgeGet, knowledgePost, knowledgePut } from './http';

const productBase = '/oversea-knowledge/product_knowledge';

const knowledgeBaseByType: Record<ProductKnowledgeType, string> = {
  SUPPLEMENTAL_KNOWLEDGE: '/oversea-knowledge/supplemental_knowledge/knowledge',
  DOCUMENT_KNOWLEDGE: '/oversea-knowledge/document_knowledge/knowledge',
  SPU_DETAIL_KNOWLEDGE: '/oversea-knowledge/spu_detail_v2/knowledge',
};

const normalizeKnowledgeId = (type: ProductKnowledgeType, knowledgeId: string | number) =>
  type === 'DOCUMENT_KNOWLEDGE' ? knowledgeId : String(knowledgeId);

const getKnowledgePath = (type: ProductKnowledgeType, knowledgeId: string | number) =>
  `${knowledgeBaseByType[type]}/${normalizeKnowledgeId(type, knowledgeId)}`;

export const searchProductKnowledgeProducts = (input: ProductKnowledgeProductSearchInput) =>
  knowledgePost<ProductKnowledgeProductPage>(`${productBase}/products/search`, input);

export const searchProductKnowledge = (input: ProductKnowledgeSearchInput) =>
  knowledgePost<ProductKnowledgeDetailPage>(`${productBase}/products/knowledge/search`, input);

export const searchProductKnowledgeScopeProducts = (input: ProductKnowledgeScopeSearchInput) =>
  knowledgePost<ProductKnowledgeScopeSearchResult>(`${productBase}/scope/products/search`, input);

export const addProductKnowledgeScopeMember = (input: ProductKnowledgeScopeMutationInput) =>
  knowledgePost<ProductKnowledgeScopeSnapshot>(`${productBase}/scope/members/add`, input);

export const removeProductKnowledgeScopeMember = (input: ProductKnowledgeScopeMutationInput) =>
  knowledgePost<ProductKnowledgeScopeSnapshot>(`${productBase}/scope/members/remove`, input);

export const searchProductKnowledgePickerProducts = (input: ProductKnowledgePickerInput) =>
  knowledgePost<ProductKnowledgeScopeSearchResult>(`${productBase}/product-picker/search`, input);

export const getProductKnowledgeDetail = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
) => knowledgeGet<ProductKnowledgeDetailByType[Type]>(getKnowledgePath(type, knowledgeId));

export const updateProductKnowledge = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
  input: ProductKnowledgeUpdateInputByType[Type],
) => knowledgePut<ProductKnowledgeDetailByType[Type]>(getKnowledgePath(type, knowledgeId), input);

/** 统一软删回执状态，契约见 oversea-knowledge docs/api/knowledge-unified-delete-frontend-api.md */
export type KnowledgeDeleteItemStatus = 'DELETED' | 'ALREADY_DELETED' | 'NOT_FOUND' | 'FAILED';

export interface KnowledgeDeleteResult {
  knowledgeId: string | number;
  status: KnowledgeDeleteItemStatus;
  message?: string;
}

/**
 * 统一软删接口为 POST /oversea-knowledge/knowledge/delete（批量，1~100 条），
 * 没有 RESTful DELETE 路由；这里封装为单条删除，按条目回执判定成败：
 * DELETED / ALREADY_DELETED（幂等）视为成功，NOT_FOUND / FAILED 抛错。
 */
export const deleteProductKnowledge = async (
  type: ProductKnowledgeType,
  knowledgeId: string | number,
) => {
  const { results } = await knowledgePost<{ results: KnowledgeDeleteResult[] }>(
    '/oversea-knowledge/knowledge/delete',
    { knowledgeType: type, knowledgeIds: [knowledgeId] },
  );
  const result = results?.[0];
  if (!result || result.status === 'FAILED' || result.status === 'NOT_FOUND') {
    throw new Error(result?.message || 'delete product knowledge failed');
  }
  return result;
};

export const setProductKnowledgeEnabled = <Type extends ProductKnowledgeType>(
  type: Type,
  knowledgeId: string | number,
  enabled: boolean,
) =>
  knowledgePost<ProductKnowledgeDetailByType[Type]>(
    `${getKnowledgePath(type, knowledgeId)}/${enabled ? 'enable' : 'disable'}`,
  );
